import {ChangeDetectionStrategy, Component, computed, inject, signal} from '@angular/core';
import {BZ_DIALOG_DATA, BzDialogRef, BzSelectOption} from '@bringoz/ui';
import {bzIconChevronDown, bzIconRisk} from '@bringoz/ui/icons';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';
import * as moment from 'moment-timezone';
import {PanelService} from '../../../../commons/angular/services/panel.service';
import {injectLegacy} from '../../../../commons/angular/utils/inject-legacy.util';
import type {SplitService} from '../../../../commons/services/split-service.service';
import type {
  AgentConfigurationProfilesRequestsService
} from '../../../../configuration-profiles/services/agent-configuration-profiles-requests.service';
import {BreakType} from '../../../../site-preferences/models/break-type.model';
import {AccountModel} from '../../../../account/angular/models/account.model';
import {
  AddStopDialogData,
  AddStopDialogResult,
  TimeCardEntryType,
  TimecardOption
} from '../../../types/timekeeping-dialog.type';
import {
  buildStopLocation,
  durationToMillis,
  EMPTY_STOP_LOCATION,
  isDuplicateBreakType,
  validateBreakOverlap,
  validateStopTimes
} from '../../utils/timekeeping-dialog.util';
import {
  TimecardAccountSelection,
  TimecardAccountSelectPanelComponent,
  TimecardAccountSelectPanelData
} from '../timecard-account-select-panel/timecard-account-select-panel.component';

interface FormState {
  canSubmit: boolean;
  error: string | null;
}

const HOUR_MS = 3600000;

const STOP_TYPES: { value: TimeCardEntryType; label: string }[] = [
  {value: TimeCardEntryType.Task, label: 'Task'},
  {value: TimeCardEntryType.AgentStartLocation, label: 'Start Location'},
  {value: TimeCardEntryType.AgentEndLocation, label: 'End Location'},
  {value: TimeCardEntryType.Break, label: 'Break'}
];

/** Allowed once per timecard. */
const SINGLE_STOP_TYPES = [TimeCardEntryType.AgentStartLocation, TimeCardEntryType.AgentEndLocation];

/**
 * Adds a stop to a timecard of the day: a task at an account, a break, or the agent's start/end
 * location. A task at an account the timecard already has updates that entry's times instead.
 */
require('../timekeeping-dialogs.scss');
@Component({
  standalone: false,
  selector: 'app-timecard-add-stop-dialog',
  templateUrl: './timecard-add-stop-dialog.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TimecardAddStopDialogComponent {

  readonly data = inject<AddStopDialogData>(BZ_DIALOG_DATA);
  private readonly ref = inject<BzDialogRef<AddStopDialogResult>>(BzDialogRef);
  private readonly translate = inject(TranslateService);
  private readonly panelService = inject(PanelService);
  private readonly splitService = injectLegacy<SplitService>('splitService');
  private readonly agentConfigurationProfilesRequestsService =
    injectLegacy<AgentConfigurationProfilesRequestsService>('agentConfigurationProfilesRequestsService');

  readonly StopType = TimeCardEntryType;
  readonly icons = {chevron: bzIconChevronDown, warning: bzIconRisk};
  readonly timecards: TimecardOption[] = this.data.timecards || [];
  readonly timecardOptions: BzSelectOption<string>[] = this.timecards.map((timecard) => ({
    value: timecard.id,
    label: `${timecard.shiftName} (${this.formatTime(timecard.clockInTime)} - ${this.formatTime(timecard.clockOutTime)})`
  }));
  readonly hasAgentAddress = !!this.data.agentAddress?.location;

  readonly selectedTimecardId = signal<string>(this.data.selectedTimecardId || this.timecards[0]?.id);
  readonly stopType = signal<TimeCardEntryType>(TimeCardEntryType.Task);
  readonly referenceDate = signal<number>(this.data.referenceDate);
  readonly selectedAccount = signal<AccountModel | null>(null);
  readonly arrivalTime = signal<number | null>(null);
  readonly departureTime = signal<number | null>(null);
  readonly serviceHours = signal<string>('0');
  readonly serviceMinutes = signal<string>('0');
  readonly breakTypes = signal<BreakType[]>([]);
  readonly isLoadingBreakTypes = signal(false);
  readonly selectedBreakTypeId = signal<string | null>(null);

  readonly selectedTimecard = computed(() => _.find(this.timecards, {id: this.selectedTimecardId()}));

  /** Start and End Location are allowed once per timecard; tasks and breaks have no limit. */
  readonly stopTypeOptions = computed<BzSelectOption<TimeCardEntryType>[]>(() => {
    const entryTypes = this.selectedTimecard()?.entryTypes || [];
    return STOP_TYPES.map(({value, label}) => ({
      value,
      label: this.translate.instant(label),
      disabled: _.includes(SINGLE_STOP_TYPES, value) && _.includes(entryTypes, value)
    }));
  });

  readonly breakTypeOptions = computed<BzSelectOption<string>[]>(() => this.breakTypes().map((breakType) => ({
    value: breakType.id,
    label: `${breakType.name} (${breakType.paid ? 'Paid' : 'Unpaid'})`
  })));

  /** Index among the timecard's active entries - the caller maps it back to the full list. */
  readonly existingEntryIndex = computed(() => {
    const accountId = this.selectedAccount()?.id;
    const index = accountId ? _.findIndex(this.selectedTimecard()?.entries, {accountId}) : -1;
    return index >= 0 ? index : null;
  });

  readonly duplicateBreakTypeWarning = computed(() => this.stopType() === TimeCardEntryType.Break
    && isDuplicateBreakType(this.selectedBreakTypeId(), this.selectedTimecard()));

  readonly formState = computed<FormState>(() => this.validate());

  constructor() {
    this.setDefaultTimes();
  }

  // ******************************
  //        Fields
  // ******************************

  onTimecardSelect(timecardId: unknown) {
    this.selectedTimecardId.set(timecardId as string);
    this.referenceDate.set(this.selectedTimecard()?.clockInTime || this.data.referenceDate);
    // Start/End Location may already exist in the newly selected timecard
    const currentOption = _.find(this.stopTypeOptions(), {value: this.stopType()});
    if (currentOption?.disabled) {
      this.stopType.set(TimeCardEntryType.Task);
    }
    this.prefillFromExistingEntry();
    this.setDefaultTimes();
  }

  onStopTypeChange(stopType: unknown) {
    this.stopType.set(stopType as TimeCardEntryType);
    if (stopType === TimeCardEntryType.Task) {
      this.setDefaultTimes();
      return;
    }

    this.selectedAccount.set(null);
    this.resetServiceTime();
    if (stopType === TimeCardEntryType.Break) {
      this.selectedBreakTypeId.set(null);
      this.loadBreakTypes();
      this.setDefaultTimes();
    } else {
      this.arrivalTime.set(null);
      this.departureTime.set(null);
    }
  }

  onOpenAccountPanel(event: MouseEvent) {
    const data: TimecardAccountSelectPanelData = {selectedId: this.selectedAccount()?.id};
    this.panelService.open(TimecardAccountSelectPanelComponent, event, data, {
      position: {originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4}
    }).afterClose$.subscribe((selection: TimecardAccountSelection | null) => {
      if (selection?.account) {
        this.selectedAccount.set(selection.account);
        this.prefillFromExistingEntry();
      }
    });
  }

  onBreakTypeSelect(breakTypeId: unknown) {
    this.selectedBreakTypeId.set(breakTypeId as string);
  }

  /** Pushes the departure an hour past a new arrival that reaches it, but not past clock-out. */
  onArrivalChange(time: number) {
    this.arrivalTime.set(time);
    const departure = this.departureTime();
    if (departure && time >= departure) {
      const clockOutTime = this.selectedTimecard()?.clockOutTime;
      const newDeparture = time + HOUR_MS;
      this.departureTime.set(clockOutTime && newDeparture > clockOutTime ? clockOutTime : newDeparture);
    }
  }

  onDepartureChange(time: number) {
    this.departureTime.set(time);
  }

  onCancel() {
    this.ref.close();
  }

  onSubmit() {
    if (!this.formState().canSubmit) {
      return;
    }
    this.ref.close(this.buildResult());
  }

  // ******************************
  //        Private
  // ******************************

  private validate(): FormState {
    const timecard = this.selectedTimecard();
    const hasTimecard = !this.timecards.length || !!timecard;
    const arrival = this.arrivalTime();
    const departure = this.departureTime();

    switch (this.stopType()) {
      case TimeCardEntryType.Task: {
        if (!this.selectedAccount() || !arrival || !departure || !hasTimecard) {
          return {canSubmit: false, error: null};
        }
        const error = validateStopTimes(arrival, departure, timecard?.clockInTime, timecard?.clockOutTime);
        return {canSubmit: !error, error};
      }
      case TimeCardEntryType.Break: {
        if (!hasTimecard) {
          return {canSubmit: false, error: null};
        }
        if (!this.selectedBreakTypeId()) {
          return {canSubmit: false, error: 'SELECT_BREAK_TYPE_REQUIRED'};
        }
        if (!arrival || !departure) {
          return {canSubmit: false, error: 'SELECT_BREAK_TIMES_REQUIRED'};
        }
        const error = validateStopTimes(arrival, departure, timecard?.clockInTime, timecard?.clockOutTime)
          || validateBreakOverlap(arrival, departure, timecard);
        return {canSubmit: !error, error};
      }
      default:
        return {canSubmit: hasTimecard, error: null};
    }
  }

  private buildResult(): AddStopDialogResult {
    const timecardId = this.selectedTimecardId();
    switch (this.stopType()) {
      case TimeCardEntryType.Task: {
        const account = this.selectedAccount();
        const serviceTime = durationToMillis(this.serviceHours(), this.serviceMinutes());
        return {
          accountId: account.id,
          accountName: account.name || '',
          arrival: this.arrivalTime(),
          departure: this.departureTime(),
          location: buildStopLocation(account.address, (account.address as any)?.formattedAddress),
          timecardId,
          isUpdateExisting: this.existingEntryIndex() !== null,
          existingEntryIndex: this.existingEntryIndex(),
          stopType: TimeCardEntryType.Task,
          serviceTime: serviceTime || undefined
        };
      }
      case TimeCardEntryType.Break: {
        const breakType = _.find(this.breakTypes(), {id: this.selectedBreakTypeId()});
        return {
          location: EMPTY_STOP_LOCATION,
          timecardId,
          isUpdateExisting: false,
          stopType: TimeCardEntryType.Break,
          arrival: this.arrivalTime(),
          departure: this.departureTime(),
          breakTypeId: this.selectedBreakTypeId(),
          breakTypePaid: !!breakType?.paid
        };
      }
      default: {
        const address = this.hasAgentAddress ? this.data.agentAddress : null;
        return {
          location: buildStopLocation(address, address?.place),
          timecardId,
          isUpdateExisting: false,
          stopType: this.stopType()
        };
      }
    }
  }

  /** Clock-in and clock-out of the timecard, unless an existing entry's times are being edited. */
  private setDefaultTimes() {
    if (this.existingEntryIndex() !== null) {
      return;
    }
    const timecard = this.selectedTimecard();
    if (timecard?.clockInTime) {
      this.arrivalTime.set(timecard.clockInTime);
    }
    if (timecard?.clockOutTime) {
      this.departureTime.set(timecard.clockOutTime);
    }
  }

  private prefillFromExistingEntry() {
    const index = this.existingEntryIndex();
    const entry = index !== null ? this.selectedTimecard()?.entries[index] : null;
    if (!entry) {
      return;
    }
    if (!this.arrivalTime() && entry.startTime) {
      this.arrivalTime.set(entry.startTime);
    }
    if (!this.departureTime() && entry.endTime) {
      this.departureTime.set(entry.endTime);
    }
  }

  private resetServiceTime() {
    this.serviceHours.set('0');
    this.serviceMinutes.set('0');
  }

  /** Break types of the agent's configuration profile, fetched once. */
  private loadBreakTypes() {
    if (this.breakTypes().length || this.isLoadingBreakTypes() || !this.data.agentId) {
      return;
    }
    this.isLoadingBreakTypes.set(true);
    this.agentConfigurationProfilesRequestsService.getConfigurationByAgent(this.data.agentId)
      .then((profile: any) => this.agentConfigurationProfilesRequestsService.getBreakTypesForProfile(profile.id))
      .then((breakTypes: BreakType[]) => this.breakTypes.set(_.reject(breakTypes || [], 'archive')))
      .catch(() => this.breakTypes.set([]))
      .finally(() => this.isLoadingBreakTypes.set(false));
  }

  private formatTime(timestamp: number): string {
    if (!timestamp) {
      return '--:--';
    }
    const format = this.splitService.getDateAndTimeConfig().time || 'hh:mm A';
    return this.data.zoneId ? moment(timestamp).tz(this.data.zoneId).format(format) : moment(timestamp).format(format);
  }
}
